import React from "react";
import {
  Form,
  Button
} from 'reactstrap';
import { withNavigation } from '../Components/helpers/withNavigation';
import Body from "../Components/layout/Body";
import TrainingNavigation from "../Components/layout/TrainingNavigation";
import {VideoModelPageControls} from '../Components/layout/VideoModelPage';
import '../App.css';
import { isGuid } from "../App";
import { TourProvider } from '@reactour/tour';
import { TourBridge, SkipHiddenTourSteps } from '../utils/TourHelpers';

function VideoModelStepWithTour(props){
  const steps= [
    {
      selector:'.function-bar',
      content:(
      <>
        <li>Home makes the app go back to selecting trainings on the homepage, where you can choose between trainings you have created to view, edit, or export.</li>
        {!props.supportsFSAccess && (
          <li>Click Download Backup to save a copy of your data (your browser will download a file)</li>
        )}
        <li>Help to show this tour again.</li>
      </>
      )
    },
    {
      selector: '.instruct-navigation-listgroup',
      content:(
        <>
          <li>This is the navigation sidebar showing the steps in the current training.</li>
          <li>You can select any step to navigate directly to it.</li>
        </>      
      ),
    },{
      selector: '.main-form',
      content:(
        <>
          <li>This is the main content area showing the video model for the current training step.</li>
          <li>You can capture or change the video model using the Capture Video button.</li>
        </>
      ),
    },
    {
      selector: '.instruct-control-container',
      content:(
        <>
          <li>Use the Set Start and Set End buttons to define the portion of the video to be used as the model.</li>
          <li>The Start/End time is set the current timestamp of the video above.</li>
          <li>The Return button returns you to the associated checklist step, or main editing view.</li>
        </>
      ),
    }
  ]
  return (
    <div className={'training-container'}>
      <TourProvider 
        steps={steps} 
        showBadge={false}
        padding={10} 
        scrollSmooth 
        className="my-custom-tour" 
        onClickClose={() => {props.updateState({showTour: false});}} 
        onClickMask={() => {props.updateState({showTour: false});}} 
      >
        <SkipHiddenTourSteps/>
        <TourBridge showTour={props.showTour} incognitoBrowser={props.incognitoBrowser} updateState={props.updateState}>
          <VideoModelStep {...props} />
        </TourBridge>
      </TourProvider>
    </div>
  );
}

class VideoModelStep extends React.Component {
  constructor(props) {
    super(props);

    const saveMessage = {
      icon:'success',
      title:'Progress Saved',
      label: '',
      message: '',
      duration: 3000
    };

    this.state = {
      playbackSpeed: 1.0,
      mediaCompleted: false,
      start: this.props.currentStep?.Start ?? 0,
      end: this.props.currentStep?.End ?? 0,
      resolvedVideoSrc: null,
      volume: 1.0,
      muted: false,
      saveMessage: saveMessage
    };
  }

  componentDidMount() {
    this._cancelled = false;
    this.resolveVideo(this.props.currentStep?.VideoFilepath);

    if (!this.props.currentStep?.VideoFilepath) {
      const curURL = new URL(window.location.href);
      console.log('VideoModelStep: redirecting to /camera', {
        hasVideo: !!this.props.currentStep?.VideoFilepath,
        navigateType: typeof this.props.navigate
      });

      try {
        if (window.location.pathname !== '/camera') {
          if (!this._redirected) {
            this._redirected = true;
            setTimeout(() => {
              this.props.navigate({
                pathname: '/camera',
                search: curURL.search
              });
            }, 10);
          }
        }
      } catch (err) {
        // Fallback: force navigation if navigate throws or is unavailable
        console.warn('navigate failed, falling back to window.location.href', err);
        window.location.href = '/camera' + curURL.search;
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.currentStep?.VideoFilepath !== this.props.currentStep?.VideoFilepath) {
      this.resolveVideo(this.props.currentStep?.VideoFilepath);
    }
  }

  async resolveVideo(videoFilePath) {
    if (!videoFilePath) {
      this.setState({ resolvedVideoSrc: null });
      return;
    }

    if (isGuid(videoFilePath)) {
      try {
        const media = await this.props.getMediaById(videoFilePath);
        if (!this._cancelled && media?.file) {
          const url = URL.createObjectURL(media.file);
          this.setState({ resolvedVideoSrc: url });
        }
      } catch (err) {
        console.error("Failed to load audio:", err);
        if (!this._cancelled) {
          this.setState({ resolvedVideoSrc: null });
        }
      }
    } else {
      this.setState({ resolvedVideoSrc: videoFilePath });
    }
  }

  seekMediaTo = (seconds) => {
    if (this.videoPlayer) {
      if('fastSeek' in this.videoPlayer)
        this.videoPlayer.fastSeek(seconds, 'seconds');
      else
        this.videoPlayer.currentTime = seconds;
    }
  }

  onMediaEnded = () => {
    this.setState({mediaCompleted: true});
  }

  onProgressChanged = (state) => {
    if(this.state.resolvedVideoSrc){
      if( (this.props.currentStep.End / 1000) < this.videoPlayer.duration &&  state.playedSeconds >= (this.props.currentStep.End / 1000)){
        this.videoPlayer.pause();
      }
    }
    this.setState(state);
  }

  onPlay = () => { 
    if(this.videoPlayer.currentTime * 1000 < this.props.currentStep.Start){
      this.seekMediaTo(this.props.currentStep.Start / 1000, 'seconds');
    }
    if(this.videoPlayer.currentTime * 1000 >= this.props.currentStep.End && this.props.currentStep.End > 0){
      this.seekMediaTo(this.props.currentStep.Start / 1000, 'seconds');
    }
  }

  onPause = () => { }

  onReady = () => {
    if(this.state.resolvedVideoSrc){
      if(!this.videoPlayer.paused){ return; }
      if(this.props.currentStep.Start)
        this.seekMediaTo(this.props.currentStep.Start / 1000, 'seconds');
      this.setState({mediaCompleted: false});
    }
  }

  onSetStartClick = () => {
    var currentStep = this.props.currentStep;
    if(currentStep.Start && currentStep.Start > 0){
      currentStep.Start = 0;
      this.setState({start: 0});
    }
    else{
      currentStep.Start = this.videoPlayer.currentTime * 1000;
      this.setState({start: this.videoPlayer.currentTime * 1000});
    }
    this.props.updateState({currentStep});
  }

  onSetEndClick = () => {
    var currentStep = this.props.currentStep;
    var durationMs = this.videoPlayer.duration * 1000;
    if(currentStep.End && currentStep.End < durationMs){
      currentStep.End = durationMs;
      this.setState({end: durationMs});
    }else{
      currentStep.End = this.videoPlayer.currentTime * 1000;
      this.setState({end: this.videoPlayer.currentTime * 1000});
    }
    this.props.updateState({currentStep});
  }

  handleNextClick = () => {
    const currentStep = this.props.currentTraining?.Steps?.find(step => step.Index === this.props.currentStep.Index - 1);
    this.props.updateState({currentStep, tempMessage: this.state.saveMessage});
    const curURL = new URL(window.location.href);
    if(this.props.uiInUse === 'UI: Accordion'){
      this.props.navigate({
        pathname: '/main-accordion',
        search: curURL.search
      });
    }else{
      this.props.navigate({
        pathname: '/checklist-step',
        search: curURL.search
      });
    }
  }
  
  onNavigationClick = (selectedStep) => {
    if(this.props.uiInUse === 'UI: Accordion'){
        return;
    }
    const currentStep = selectedStep;
    this.props.updateState({currentStep, tempMessage: this.state.saveMessage});
    const curURL = new URL(window.location.href);
    switch(selectedStep.StepType){
      case 2:
        this.props.navigate({
          pathname: '/information-step',
          search: curURL.search
        });
        break;
      case 3:
        this.props.navigate({
          pathname: '/checklist-step',
          search: curURL.search
        });
        break;
      default:
        break;
    }
  }

  openMediaCapture = () => {
    const curURL = new URL(window.location.href);
    this.props.navigate({
      pathname: '/camera',
      search: curURL.search
    });
  }
    
  togglePlay = () => {
    if (!this.videoPlayer) return;
    if (this.videoPlayer?.paused) {
      const startSec = (this.props.currentStep?.Start ?? 0) / 1000;
      if (this.videoPlayer.currentTime < startSec) {
        this.seekMediaTo(startSec);
      }
      this.videoPlayer.play();
    } else {
      this.videoPlayer.pause();
    }
  }

  handleProgressClick = (e) => {
    if (!this.videoPlayer) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX ?? (e.touches && e.touches[0].clientX)) - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
    const dur = this.videoPlayer.duration || 0;
    const sec = pct * dur;
    this.seekMediaTo(sec);
    // update visual immediately
    this.setState({ playedSeconds: sec });
  }

  formatTime = (s) => {
    if (s === undefined || s === null || isNaN(s)) return '--:--';
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  toggleMute = () => {
    if (!this.videoPlayer) return;
    const muted = !this.videoPlayer.muted;
    this.videoPlayer.muted = muted;
    this.setState({ muted });
  }

  onVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    if (this.videoPlayer) {
      this.videoPlayer.volume = v;
      this.videoPlayer.muted = v === 0;
    }
    this.setState({ volume: v, muted: v === 0 });
  }

  adjustVolume = (delta) => {
    const cur = this.state.volume ?? (this.videoPlayer?.volume ?? 1);
    const v = Math.max(0, Math.min(1, cur + delta));
    if (this.videoPlayer) {
      this.videoPlayer.volume = v;
      this.videoPlayer.muted = v === 0;
    }
    this.setState({ volume: v, muted: v === 0 });
  }
  
  render() {
    console.log("rendering VideoModelStep, with props: ", this.props, " state: ", this.state);
    if(!this.props.currentTraining){
      return (<div><p>No training selected.</p></div>);
    }
    return (
      <Body
        contentComponent={
        <Form id='main-form' className='main-form' style={{backgroundColor: "yellow"}}>
          {this.state.resolvedVideoSrc ? 
          <div id='video-container'
            ref={contentContainer =>{ this.contentContainer = contentContainer}} 
            className='content-container d-flex flex-column align-items-stretch'>
            <div style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', padding: 8 }}>
              <video
                ref={videoPlayer => { this.videoPlayer = videoPlayer }}
                src={this.state.resolvedVideoSrc}
                controls={false}
                style={{
                  width: "100%",
                  height: "auto",
                  maxWidth: "100%",
                  maxHeight: "45vh",
                  objectFit: "contain",
                  display: "block"
                }}
                onEnded={this.onMediaEnded}
                onPlay={this.onPlay}
                onPause={this.onPause}
                onLoadedMetadata={this.onReady}
                onTimeUpdate={(e) => {
                  const playedSeconds = e.target.currentTime;
                  this.onProgressChanged({ playedSeconds });
                }}
              />
            </div>

            {/* custom controls */}
            <div className="video-controls" style={{ padding: 8, background: 'transparent', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* wrapper: left = play button (fills height), right = progress + lower controls */}
              <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                {/* Play button - will stretch to match the height of the right column */}
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={this.togglePlay}
                  aria-label={this.videoPlayer?.paused ? 'Play' : 'Pause'}
                  title={this.videoPlayer?.paused ? 'Play' : 'Pause'}
                  style={{
                    minWidth: 48,
                    width: 'auto',
                    aspectRatio: '1 / 1',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                    padding: 8
                  }}
                >
                  {this.videoPlayer?.paused ? '▶' : '⏸'}
                </button>

                {/* Right column: progress bar on top, controls below */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 56 }}>
                  <div
                    className="progress-bar"
                    role="slider"
                    aria-valuemin={0}
                    aria-valuemax={this.videoPlayer?.duration || 0}
                    onClick={this.handleProgressClick}
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: 12,
                      background: '#444',
                      borderRadius: 6,
                      cursor: 'pointer',
                      overflow: 'visible'
                    }}
                  >
                    {/* highlight between start and end (extends beyond progress bar) */}
                    {this.videoPlayer?.duration > 0 && (() => {
                      const dur = this.videoPlayer.duration || 0;
                      const startPct = Math.max(0, Math.min(100, ((this.props.currentStep?.Start ?? 0) / 1000) / dur * 100));
                      const endPct = Math.max(0, Math.min(100, ((this.props.currentStep?.End ?? (dur * 1000)) / 1000) / dur * 100));
                      const widthPct = Math.max(0, endPct - startPct);
                      return (
                        <div
                          className="range-highlight"
                          style={{
                            position: 'absolute',
                            left: `${startPct}%`,
                            width: `${widthPct}%`,
                            // extend above and below the bar
                            top: -8,
                            height: 12 + 16, // original bar height (12) + extension (16)
                            borderRadius: 8,
                            background: 'rgba(0,150,136,0.25)',
                            pointerEvents: 'none',
                            zIndex: 0,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
                          }}
                        />
                      );
                    })()}

                    {/* played portion */}
                    <div
                      className="played"
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${(this.state.playedSeconds && this.videoPlayer?.duration) ? (this.state.playedSeconds / this.videoPlayer.duration * 100) : 0}%`,
                        background: '#00bcd4',
                        transition: 'width 100ms linear',
                        zIndex: 2
                      }}
                    />
                  </div>

                  {/* lower control row */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 40 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ fontSize: 12, color: '#666' }}>{this.formatTime(this.state.playedSeconds)}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>/</div>
                      <div style={{ fontSize: 12, color: '#666' }}>{this.formatTime(this.videoPlayer?.duration)}</div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={this.toggleMute}
                        aria-pressed={this.state.muted}
                        title={this.state.muted ? 'Unmute' : 'Mute'}
                      >
                        {this.state.muted ? '🔈' : '🔊'}
                      </button>

                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => this.adjustVolume(-0.1)}>-</button>

                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={this.state.volume ?? (this.videoPlayer?.volume ?? 1)}
                        onChange={this.onVolumeChange}
                        style={{ width: 140 }}
                        aria-label="Volume"
                      />

                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => this.adjustVolume(0.1)}>+</button>

                      <div style={{ minWidth: 44, textAlign: 'center', fontSize: 12, color: '#666' }}>
                        {Math.round((this.state.volume ?? (this.videoPlayer?.volume ?? 1)) * 100)}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          :
            <div className='content-container d-flex justify-content-center align-items-center'>
            <h3>Please add a video model for {this.props.currentStep?.Title}.</h3>
            </div>
          }
        </Form>
        }
        controlsComponent={
          <VideoModelPageControls 
            VideoStartText={this.props.currentStep?.Start ? "Reset Start" : 'Set Start'}
            handleSetStartClick={this.onSetStartClick}
            VideoEndText={this.props.currentStep?.End && this.videoPlayer?.duration && this.props.currentStep.End < this.videoPlayer.duration * 1000 ? `Reset End` : "Set End"}
            handleSetEndClick={this.onSetEndClick}
            NextStepText={this.props.uiInUse === 'UI: Accordion' ? 'Return to Editing Training' : "Return to Checklist Step"}
            handleNextClick={this.handleNextClick}
            openMediaCapture={this.openMediaCapture}
            videoButtonText={this.state.resolvedVideoSrc ? "Update Video" : "Add Video"}
          />
        }
        sidebarComponent={
          <TrainingNavigation
            steps={this.props.currentTraining?.Steps}
            currentStep={this.props.currentStep?.Index}
            handleItemClick={this.onNavigationClick}
          />
        }
        // controls are provided inline below the video (custom controls)
      />
    );
  }
}
export default withNavigation(VideoModelStepWithTour);